import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { error } from '../../../../ErrorHandling';
import { errorAlert, FlashAlert } from '../../../../Notifications/Types';
import { TPlainTextKey } from '../../../../Translations';
import {
  fileSelected,
  fileSelectFailed,
  fileSelectSucceeded,
  ImportedParticipantsResource,
  loading,
  ParticipantImportFileRow,
  State,
  submit,
  submitSucceeded,
  uploadingReady,
  viewReport,
  ViewReportAction,
  waiting,
} from './Types';

class ImportParticipantsStore {
  @observable
  state: State = waiting();

  constructor() {
    makeObservable(this);
  }

  @action
  uploadingReady = () => {
    switch (this.state.kind) {
      case 'loading':
      case 'file-select-succeeded':
      case 'view-report':
        this.state = uploadingReady();
        break;
      case 'uploading-ready':
      case 'waiting':
      case 'error':
      case 'file-selected':
      case 'file-select-failed':
      case 'submit':
      case 'submit-succeeded':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  fileSelected = (file: File) => {
    switch (this.state.kind) {
      case 'uploading-ready':
      case 'file-select-failed':
        this.state = fileSelected(file);
        break;
      case 'waiting':
      case 'error':
      case 'loading':
      case 'file-selected':
      case 'file-select-succeeded':
      case 'submit':
      case 'submit-succeeded':
      case 'view-report':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loading = () => {
    switch (this.state.kind) {
      case 'waiting':
        this.state = loading();
        break;
      case 'error':
      case 'loading':
      case 'file-selected':
      case 'file-select-failed':
      case 'file-select-succeeded':
      case 'submit':
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  fileSelectSucceeded = (rows: ParticipantImportFileRow[], filename: string) => {
    switch (this.state.kind) {
      case 'file-selected':
        this.state = fileSelectSucceeded(rows, filename);
        break;
      case 'waiting':
      case 'error':
      case 'loading':
      case 'file-select-failed':
      case 'file-select-succeeded':
      case 'submit':
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  fileSelectFailed = (errorMessage: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'file-selected':
        this.state = fileSelectFailed(errorMessage);
        break;
      case 'waiting':
      case 'error':
      case 'loading':
      case 'file-select-failed':
      case 'file-select-succeeded':
      case 'submit':
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submit = () => {
    switch (this.state.kind) {
      case 'file-select-succeeded':
        this.state = submit(this.state.rows, this.state.filename);
        break;
      case 'file-selected':
      case 'waiting':
      case 'error':
      case 'loading':
      case 'file-select-failed':
      case 'submit':
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submitSucceeded = (importedParticipantsResource: ImportedParticipantsResource) => {
    switch (this.state.kind) {
      case 'view-report':
      case 'submit':
        this.state = submitSucceeded(this.state, importedParticipantsResource);
        break;
      case 'file-select-succeeded':
      case 'file-selected':
      case 'waiting':
      case 'error':
      case 'loading':
      case 'file-select-failed':
      case 'submit-succeeded':
      case 'uploading-ready':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  viewReport = (view: ViewReportAction) => {
    switch (this.state.kind) {
      case 'submit-succeeded':
        this.state = viewReport(this.state, view);
        break;
      case 'file-select-succeeded':
      case 'file-selected':
      case 'waiting':
      case 'error':
      case 'loading':
      case 'file-select-failed':
      case 'submit':
      case 'uploading-ready':
      case 'view-report':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (msg: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'error':
      case 'loading':
      case 'waiting':
      case 'file-select-failed':
      case 'file-selected':
      case 'file-select-succeeded':
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
        break;
      case 'submit':
        this.state = error(msg);
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get uploadButtonDisabled(): boolean {
    switch (this.state.kind) {
      case 'file-selected':
        return true;
      case 'error':
      case 'loading':
      case 'waiting':
      case 'file-select-failed':
      case 'file-select-succeeded':
      case 'submit':
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
        return false;
    }
  }

  @computed
  get importButtonDisabled(): boolean {
    switch (this.state.kind) {
      case 'submit':
        return true;
      case 'file-selected':
      case 'error':
      case 'loading':
      case 'waiting':
      case 'file-select-failed':
      case 'file-select-succeeded':
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
        return false;
    }
  }

  @computed
  get fileSelectFailedMessage(): Maybe<TPlainTextKey> {
    switch (this.state.kind) {
      case 'file-select-failed':
        return just(this.state.errorMessage);
      case 'file-selected':
      case 'error':
      case 'loading':
      case 'waiting':
      case 'file-select-succeeded':
      case 'submit':
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
        return nothing();
    }
  }

  @computed
  get notification(): Maybe<FlashAlert> {
    switch (this.state.kind) {
      case 'error':
        return just(this.state).map(errorAlert);
      case 'loading':
      case 'waiting':
      case 'file-selected':
      case 'file-select-failed':
      case 'file-select-succeeded':
      case 'submit':
      case 'submit-succeeded':
      case 'uploading-ready':
      case 'view-report':
        return nothing();
    }
  }
}

export default ImportParticipantsStore;
